import { useBoolean, useToast } from '@chakra-ui/react'
import * as crypto from 'crypto-js'
import {
  createContext,
  FC,
  useCallback,
  useEffect,
  useMemo,
  useState
} from 'react'
import { AiOutlineBarChart } from 'react-icons/ai'
import { useImmer } from 'use-immer'

import useSessions from '@hook/shared/use-sessions'

import paramsHelper from '@helper/params'
import { mergeStoredHeaders } from '@helper/util/cryptoStorange'
import mrp from '@service/mrp'

import {
  ITableHeader,
  ITableGridAction
} from '@base/@types/components/Layout/TableGrid'
import { ILayoutToolsAction } from '@base/@types/components/Layout/Tools'
import {
  IMrpOportunityContext,
  IParams,
  IUpdateParams
} from '@base/@types/providers/Mrp/OportunityProvider'
import mrpOportunityHeader from '@base/variables/headers/mrp/oportunity'

const MrpOportunityProvider: FC = ({ children }) => {
  const { register } = useSessions()

  const [defaoutDesiredPeriod, setDefaoutDesiredPeriod] = useState(1)
  const [loading, setLoading] = useState(false)
  const [filters, setFilters] = useBoolean(true)
  const [query, setQuery] = useState('')
  const [fetching, _setFetching] = useState(false)
  const toast = useToast()

  const [filename, setFilename] = useState('MRP Oportunidade')

  const [data, updateData] = useImmer<any>({})

  const [params, updateParams] = useImmer<IParams>({
    lojasIds: [],
    empresasIds: [],
    fornecedoresIds: [],
    produtosIds: [],
    eTipoSimulacaoOportunidade: [],
    valorDesejado: 1,
    taxaDesconto: 1,
    prazoMedioPagamentoAtual: 1,
    prazoMedioPagamentoNegociado: 1,
    parcelas: 1,
    maximoDeMeses: 1
  })

  const filtersList = useMemo<any[]>(() => {
    const selectedValues = [
      {
        id: 'valorDesejado',
        label: 'Valor desejado da compra',
        type: 'input',
        isRequired: true,
        defaultValue: 1,

        min: 1
      },
      {
        id: 'taxaDesconto',
        label: 'Taxa de desconto a.m (%)',
        type: 'number-input',
        isRequired: true,
        defaultValue: 1,
        min: 1
      },
      {
        id: 'prazoMedioPagamentoAtual',
        label: 'Prazo médio de pagamento atual (dias)',
        type: 'number-input',
        isRequired: true,
        defaultValue: 1,

        min: 1
      },
      {
        id: 'prazoMedioPagamentoNegociado',
        label: 'Prazo médio de pagamento negociado (dias)',
        type: 'number-input',
        isRequired: true,
        defaultValue: 1,

        min: 1
      },
      {
        id: 'parcelas',
        label: 'Nº de parcelas a receber',
        type: 'number-input',
        isRequired: true,
        max: 12,
        defaultValue: 1,

        min: 1
      },
      {
        id: 'maximoDeMeses',
        label: 'Nº máximo de meses',
        type: 'number-input',
        isRequired: true,
        max: 12,
        defaultValue: 1,

        min: 1
      }
    ]

    const data = [
      {
        id: 'empresasIds',
        label: 'Empresa',
        filterId: 'companies',
        type: 'select',
        isMulti: true,
        isDisabled: params?.lojasIds?.length > 0
      },
      {
        id: 'lojasIds',
        label: 'Centro',
        filterId: 'centers',
        type: 'select',
        isMulti: true,
        isDisabled: params?.empresasIds?.length > 0
      },
      {
        id: 'fornecedoresIds',
        label: 'Fornecedor',
        filterId: 'providersForPurchase',
        type: 'select',
        isMulti: true
      },
      {
        id: 'produtosIds',
        label: 'Produto',
        type: 'select-api-post',
        apiUrl: '/v1/Produto/Compras',
        isMulti: true
      },
      {
        id: 'eTipoSimulacaoOportunidade',
        label: 'Tipo de simulação',
        filterId: 'simulationTypes',
        type: 'select',
        pagination: true,
        isRequired: true,
        limit: 1,
        isMulti: true
      },
      ...(params?.eTipoSimulacaoOportunidade?.length === 1
        ? selectedValues
        : [])
    ]

    return data
  }, [params])

  // A configuração salva entra já na inicialização do estado. Restaurá-la
  // depois, em efeito ou durante a busca, descartava a seleção que o usuário
  // tinha acabado de aplicar.
  const [headers, updateHeaders] = useImmer<ITableHeader[]>(() => {
    try {
      const stored = localStorage.getItem('mrpOportunityHeader')

      return stored
        ? mergeStoredHeaders(mrpOportunityHeader, JSON.parse(stored))
        : mrpOportunityHeader
    } catch {
      return mrpOportunityHeader
    }
  })

  const clearAllFilters = () => {
    updateParams({
      ...params,
      lojasIds: [],
      empresasIds: [],
      fornecedoresIds: [],
      produtosIds: [],
      eTipoSimulacaoOportunidade: [],
      valorDesejado: 1,
      taxaDesconto: 1,
      prazoMedioPagamentoAtual: 1,
      prazoMedioPagamentoNegociado: 1,
      parcelas: 1,
      maximoDeMeses: 1
    })

    setDefaoutDesiredPeriod(1)
    if (localStorage.getItem('mrpOportunityParams')) {
      localStorage.setItem(
        'mrpOportunityParams',
        crypto.AES.encrypt(
          JSON.stringify({
            ...params,
            lojasIds: [],
            empresasIds: [],
            fornecedoresIds: [],
            produtosIds: [],
            eTipoSimulacaoOportunidade: [],
            valorDesejado: 1,
            taxaDesconto: 1,
            prazoMedioPagamentoAtual: 1,
            prazoMedioPagamentoNegociado: 1,
            parcelas: 1,
            maximoDeMeses: 1
          }),
          'CHAVE'
        ).toString()
      )
    }
  }

  const handleUpdateParams = ({ name, value }: IUpdateParams) => {
    name === 'valorDesejado' && setDefaoutDesiredPeriod(value)
    updateParams(draft => {
      draft[name] = value
    })
    // if (localStorage.getItem('mrpOportunityParams')) {
    //   const newParams: IParams[] = JSON.parse(
    //     crypto.AES.decrypt(
    //       localStorage.getItem('mrpOportunityParams'),
    //       'CHAVE'
    //     ).toString(crypto.enc.Utf8) || ''
    //   )
    //   localStorage.setItem(
    //     'mrpOportunityParams',
    //     crypto.AES.encrypt(
    //       JSON.stringify(JSON.stringify({ ...newParams, [name]: value })),
    //       'CHAVE'
    //     ).toString()
    //   )
    // }
  }
  const handleLocalParams = useCallback(async () => {
    if (localStorage.getItem('mrpOportunityParams')) {
      const newParams = JSON.parse(
        crypto.AES.decrypt(
          localStorage.getItem('mrpOportunityParams'),
          'CHAVE'
        ).toString(crypto.enc.Utf8) || ''
      )
      updateParams(newParams)
    } else {
      localStorage.setItem(
        'mrpOportunityParams',
        crypto.AES.encrypt(JSON.stringify(params), 'CHAVE').toString()
      )
    }
  }, [updateParams, params])
  const getData = useCallback(async () => {
    // A configuração de colunas é restaurada na inicialização do estado.
    // Recarregá-la aqui descartava a seleção recém-aplicada pelo usuário.
    // await getMaintence()

    const isDisabledProgress: number =
      (params.empresasIds.length > 0 ? 1 : 0) +
      (params.fornecedoresIds.length > 0 ? 1 : 0) +
      (params.produtosIds.length > 0 ? 1 : 0) +
      (params.lojasIds.length > 0 ? 1 : 0)
    if (isDisabledProgress < 2) {
      toast({
        title: 'Atenção.',
        description: 'Selecionar ao menos dois filtro',
        status: 'warning',
        duration: 2000,
        isClosable: true
      })
      return
    }
    if (params.eTipoSimulacaoOportunidade.length === 0) {
      toast({
        title: 'Atenção.',
        description: 'Selecionar tipo Simução',
        status: 'warning',
        duration: 2000,
        isClosable: true
      })
      return
    }
    setLoading(true)

    try {
      const paramsToSend = {
        ...params,
        lojasIds: paramsHelper.parse(params.lojasIds),
        empresasIds: paramsHelper.parse(params.empresasIds),
        fornecedoresIds: paramsHelper.parse(params.fornecedoresIds),
        produtosIds: paramsHelper.parse(params.produtosIds),
        eTipoSimulacaoOportunidade: params.eTipoSimulacaoOportunidade[0].value,
        prazoMedioPagamentoNegociado: params.prazoMedioPagamentoNegociado
          ? params.prazoMedioPagamentoNegociado
          : params.prazoMedioPagamentoAtual
      }
      const { data: res } = await mrp.getOportunityData({
        params: paramsToSend
      })

      const { data } = res

      updateData((draft: any) => {
        return Object.assign(draft, data)
      })
      setFilters.off()
      register({
        name: 'MRP Oportunidade',
        path: '/mrp/oportunidade',
        loading: false
      })
      setFilters.toggle()
    } catch (error: any) {
      setFilters.toggle()

      const message =
        error.response?.status === 400
          ? error.response?.data?.message
          : 'Ocorreu um problema interno. Tente novamente mais tarde'
      toast({
        title: 'Atenção.',
        description: message,
        status: 'warning',
        duration: 2000,
        isClosable: true
      })
    }
    setLoading(false)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params, register, updateData])

  const getDiscount = useCallback(async () => {
    setLoading(true)
    const opportunity = data

    try {
      const { data: res } = await mrp.getOportunityDiscountsData({
        params: {
          compraOportunidadeCommand: opportunity
        }
      })

      const { data } = res

      updateData((draft: any) => {
        return Object.assign(draft, data)
      })

      register({
        name: 'MRP Oportunidade',
        path: '/mrp/oportunidade',
        loading: false
      })
    } catch {}

    setLoading(false)
  }, [register, data, updateData])

  const tableInteraction = useMemo<ITableGridAction[]>(() => {
    return [
      {
        type: 'default',
        icon: <AiOutlineBarChart />
      }
    ]
  }, [])

  // Recebe os headers a gravar: fechar sobre `headers` persistia o valor
  // anterior ao toggle que acabou de ser aplicado.
  const handleHeaderLocalStorange = useCallback((header: ITableHeader[]) => {
    localStorage.removeItem('mrpOportunityHeader')
    localStorage.setItem('mrpOportunityHeader', JSON.stringify(header))
  }, [])

  const toggleColumn = useCallback(
    (index: number, column: number) => {
      // Recipe do immer: o editor aplica vários toggles no mesmo lote do React
      // e recipes encadeiam sobre o resultado anterior. Derivar o próximo valor
      // de `headers` fazia só o último toggle do lote valer.
      updateHeaders(draft => {
        const col = draft[index]?.columns?.[column]
        if (col) {
          col.show = !col.show
          // Marcador estável de "configurada pelo usuário". Não pode alternar
          // junto com `show`, senão o editor perde o grupo de vista.
          col.notShowColumn = true
        }
      })
    },
    [updateHeaders]
  )

  // Persiste a configuração sempre que ela muda. Na montagem regrava o mesmo
  // valor que acabou de ser restaurado, então é inofensivo.
  useEffect(() => {
    handleHeaderLocalStorange(headers)
  }, [headers, handleHeaderLocalStorange])
  const actions = useMemo<ILayoutToolsAction[]>(() => {
    return []
  }, [])
  const contextValue = useMemo(
    () => ({
      loading,
      filters,
      fetching,

      filtersList,

      data,
      params,
      actions,

      headers,

      handleUpdateParams,
      query,
      getDiscount,
      filename,
      handleLocalParams,
      tableInteraction,

      defaoutDesiredPeriod,

      getData,
      setQuery,
      setFilename,
      toggleColumn,
      clearAllFilters,

      setFilters
    }),
    [
      loading,
      filters,
      fetching,
      filtersList,
      data,
      params,
      actions,
      headers,
      handleUpdateParams,
      query,
      getDiscount,
      filename,
      handleLocalParams,
      tableInteraction,
      defaoutDesiredPeriod,
      getData,
      setQuery,
      setFilename,
      toggleColumn,
      clearAllFilters,
      setFilters
    ]
  )

  return (
    <MrpOportunityContext.Provider value={contextValue}>
      {children}
    </MrpOportunityContext.Provider>
  )
}

export default MrpOportunityProvider
export const MrpOportunityContext = createContext<IMrpOportunityContext>(
  {} as IMrpOportunityContext
)
